import { type PathParam, generatePath as reactRouterGenPath, matchPath } from 'react-router';
import { PREFIXED_ID_ROUTE } from '../../constants/links';

/*
 * Utility function to generate a path with parameters.
 * This is a wrapper around react-router's generatePath function.
 *
 * @param path - The route path as a string.
 * @param params - An object containing the parameters to replace in the path.
 * @returns The generated path with parameters replaced.
 */
export const generatePath = <S extends string>(
	path: S,
	params?: {
		[key in PathParam<S>]: any; // TODO: EFDC-32256 - refine 'any' to be more specific, react-router stringifies params, so numbers work, but their types
		// don't allow number.  We should fix this, and update all usages.
	}
): string => reactRouterGenPath(path, params);

type PrefixedRouteMatchResult = [boolean, number];

/*
 * Utility function to match a URL against a prefixed route.
 *
 * @param url - The URL to match.
 * @param prefix - The prefix to match against.
 * @returns A tuple indicating whether the route matched and the extracted ID.
 */
export const matchPrefixedRoute = (url: string, prefix: string): PrefixedRouteMatchResult => {
	const match = matchPath(PREFIXED_ID_ROUTE, url);

	if (!match || !match.params.prefixedId) {
		return [false, NaN];
	}

	const { prefixedId } = match.params;

	if (!prefixedId.startsWith(prefix)) {
		return [false, NaN];
	}

	const idStr = prefixedId.slice(prefix.length);
	const id = Number(idStr);

	if (isNaN(id)) {
		return [false, NaN];
	}

	return [true, id];
};
